/* global google */

import { useState, useCallback } from "react";
import { useAutocompleteSuggestions } from "./use-autocomplete-suggestions";

const parseGoogleAddress = (components) => {
    const get = (type) =>
        components.find((c) => c.types.includes(type))?.longText ?? "";

    return {
        addressNumber: get("street_number"),
        addressLine1: get("route"),
        addressLine2: get("locality"),
        addressTown: get("postal_town"),
        addressCounty: get("administrative_area_level_2"),
        addressPostalCode: get("postal_code"),
        addressCountry: get("country"),
    };
};

export const useGoogleAddress = (setValue) => {
    const [inputValue, setInputValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const { suggestions, resetSession } =
        useAutocompleteSuggestions(inputValue);

    const handleInputChange = (value) => {
        setInputValue(value);
        setIsOpen(true);
    };

    const handleSelect = async (suggestion) => {
        const place = suggestion.placePrediction.toPlace();
        await place.fetchFields({
            fields: ["addressComponents", "formattedAddress", "location"],
        });

        const address = parseGoogleAddress(place.addressComponents ?? []);

        const latitude = place.location?.lat();
        const longitude = place.location?.lng();

        Object.entries(address).forEach(([key, value]) => {
            setValue(key, value, { shouldValidate: true });
        });

        if (latitude != null && longitude != null) {
            setValue("addressLatitude", latitude, { shouldValidate: true });
            setValue("addressLongitude", longitude, { shouldValidate: true });
        }

        setInputValue(place.formattedAddress ?? "");
        setIsOpen(false);
        resetSession();
    };

    const applyPosition = useCallback(
        async (latitude, longitude) => {
            setValue("addressLatitude", latitude, { shouldValidate: true });
            setValue("addressLongitude", longitude, { shouldValidate: true });

            try {
                const geocoder = new google.maps.Geocoder();

                const { results } = await geocoder.geocode({
                    location: { lat: latitude, lng: longitude },
                });

                const result = results?.[0];

                if (result) {
                    const address = parseGoogleAddress(
                        result.address_components.map((c) => ({
                            types: c.types,
                            longText: c.long_name,
                        })),
                    );

                    Object.entries(address).forEach(([key, value]) => {
                        setValue(key, value, { shouldValidate: true });
                    });

                    setInputValue(
                        address.addressTown
                            ? `${address.addressTown}, ${address.addressCountry}`
                            : "Current location",
                    );
                } else {
                    setInputValue("Current location");
                }
            } catch {
                setInputValue("Current location");
            }
        },
        [setValue],
    );

    const handleCurrentLocation = () => {
        if (!navigator.geolocation) return;

        setIsOpen(false);

        navigator.geolocation.getCurrentPosition(async (position) => {
            await applyPosition(
                position.coords.latitude,
                position.coords.longitude,
            );
        });
    };

    const clear = () => {
        setInputValue("");
        setIsOpen(false);
        resetSession();
    };

    return {
        inputValue,
        handleInputChange,
        suggestions,
        isOpen,
        handleSelect,
        applyPosition,
        handleCurrentLocation,
        clear,
    };
};
